import PropTypes from "prop-types";
import React, { Fragment } from "react";

import { sizes } from "../defines/sizes";
import { Icon, iconPropTypes } from "../Icon";
import { Separator, separatorPropTypes } from "../Separator";
import styles from "./Stepper.module.scss";

// TODO: update label columns widths dynamically (PHX-1969)
const DEFAULT_ICON_ADJACENT_COLUMN_WIDTH = "20px";
const ICON_COLUMNS_GRID_LINES_SPAN_AMOUNT = 4;
const ICON_COLUMN_INITIAL_GRID_LINE_OFFSET = 2;
const STEP_CONTENT_GRID_LINES_SPAN_AMOUNT = 4;
const STEP_CONTENT_INITIAL_GRID_LINE_OFFSET = 1;
const STEP_CONTENT_GRID_LINE_COLUMN_SPAN_OFFSET = 3;
const PROGRESS_LINE_GRID_LINES_SPAN_AMOUNT = 4;
const PROGRESS_LINE_INITIAL_GRID_LINE_OFFSET = 3;
const PROGRESS_LINE_GRID_LINE_COLUMN_SPAN_OFFSET = 3;

/**
 * The Stepper component displays a sequence of pre-defined steps. Each step consists
 * of an Icon component, a connecting line(Separator component) and optional child content.
 * A unique grid layout is used to display and align each of the elements when the child content is shown
 * via the showStepContent prop. More details can be found in the Stepper.md file.
 */

export const Stepper = (props) => {
  const {
    className,
    separatorProps = { color: "neutral-500", lineCap: "butt" },
    showStepContent = false,
    steps,
  } = props;

  return <div className={className}>{renderSteps()}</div>;

  function renderSteps() {
    return (
      <div className={styles.stepperContainer} style={getStepsLayoutStyle()}>
        {steps.map(renderIconAndLine)}
        {showStepContent && steps.map(renderStepContent)}
      </div>
    );
  }

  function renderIconAndLine(icon, stepIndex) {
    const stepGridColumnStart =
      stepIndex * ICON_COLUMNS_GRID_LINES_SPAN_AMOUNT + ICON_COLUMN_INITIAL_GRID_LINE_OFFSET;
    const stepIconStyle = showStepContent ? { gridColumn: stepGridColumnStart } : {};

    return (
      <Fragment key={stepIndex}>
        {renderProgressLine(stepIndex)}
        <div className={styles.stepIcon} style={stepIconStyle}>
          <Icon {...getIconProps(icon)} />
        </div>
      </Fragment>
    );
  }

  function renderStepContent(step, stepIndex) {
    const { children } = step;
    const stepContentGridColumnStart =
      stepIndex * STEP_CONTENT_GRID_LINES_SPAN_AMOUNT + STEP_CONTENT_INITIAL_GRID_LINE_OFFSET;
    const stepContentGridColumnEnd =
      stepContentGridColumnStart + STEP_CONTENT_GRID_LINE_COLUMN_SPAN_OFFSET;

    return (
      <div
        {...getStepContentProps(stepIndex, stepContentGridColumnStart, stepContentGridColumnEnd)}
      >
        {children}
      </div>
    );
  }

  function renderProgressLine(stepIndex) {
    const progressLineGridColumnStart =
      (stepIndex - 1) * PROGRESS_LINE_GRID_LINES_SPAN_AMOUNT +
      PROGRESS_LINE_INITIAL_GRID_LINE_OFFSET;
    const progressLineGridColumnEnd =
      progressLineGridColumnStart + PROGRESS_LINE_GRID_LINE_COLUMN_SPAN_OFFSET;
    const progressLineStyle = showStepContent
      ? { gridColumn: `${progressLineGridColumnStart} / ${progressLineGridColumnEnd}` }
      : {};

    return stepIndex ? (
      <div style={progressLineStyle}>
        <Separator {...separatorProps} />
      </div>
    ) : null;
  }

  function getStepsLayoutStyle() {
    const templateColumns = steps.reduce((previousValue, currentStep, index) => {
      const { size = "size20" } = currentStep;
      const currentIconWidth = getComputedIconSize(size);
      const adjacentColumnWidth = showStepContent ? DEFAULT_ICON_ADJACENT_COLUMN_WIDTH : "";

      return `${previousValue} ${adjacentColumnWidth} ${currentIconWidth} ${adjacentColumnWidth} ${
        index !== steps.length - 1 ? "1fr" : ""
      }`;
    }, "");

    return { gridTemplateColumns: templateColumns };
  }

  function getComputedIconSize(iconSize) {
    return sizes[iconSize] ?? iconSize;
  }

  function getIconProps(stepIcon) {
    return {
      ...stepIcon,
      ...(!showStepContent && { role: "presentation" }),
    };
  }

  function getStepContentProps(contentIndex, stepContentGridColumnStart, stepContentGridColumnEnd) {
    return {
      className: styles.stepContent,
      key: contentIndex,
      style: { gridColumn: `${stepContentGridColumnStart} / ${stepContentGridColumnEnd}` },
    };
  }
};

Stepper.displayName = "Stepper";

export const stepperPropTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** The line that will be displayed between each step. Accepts all props of the Separator component */
  separatorProps: PropTypes.shape(separatorPropTypes),

  /** Show the child content underneath each step. */
  showStepContent: PropTypes.bool,

  /** The required array of steps to display. Accepts all props of the Icon component. */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      ...iconPropTypes,
      /** Optional child content to be displayed underneath the icon. */
      children: PropTypes.node,
    })
  ).isRequired,
};

Stepper.propTypes = stepperPropTypes;
